/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  useSearch,
} from "@tanstack/react-router";
import App from "@/App";
import Main from "@/components/Main";
import Login from "@/components/auth/Login";
import { HubPage } from "@/components/hub/HubPage";
import { VacuumPage } from "@/components/hub/VacuumPage";
import { useAuth } from "@/context/auth";
import { useEffect } from "react";

// Protected route wrapper component
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = router.navigate;

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: "/login" });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <Outlet />;
}

// Auth route wrapper component
function AuthRoute() {
  const { isAuthenticated, isLoading, checkRegistrationStatus } = useAuth();
  const location = window.location.pathname;
  const navigate = router.navigate;

  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: "/" });
      return;
    }

    const checkInitialStatus = async () => {
      if (location === "/register") {
        navigate({ to: "/login" });
        return;
      }

      try {
        console.log("AuthRoute: Checking registration status...");
        const status = await checkRegistrationStatus();
        console.log("AuthRoute: Status received:", status);

        // This deployment is a private admin panel: accounts are created from
        // the server CLI, never through public registration.
      } catch (err) {
        console.error("AuthRoute: Registration check failed:", err);
      }
    };

    if (!isLoading) {
      checkInitialStatus();
    }
  }, [isLoading, isAuthenticated, location, checkRegistrationStatus, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return <Outlet />;
}

// Root route with AuthProvider
const rootRoute = createRootRoute({
  component: () => <App />,
});

// Protected routes group
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: ProtectedRoute,
});

// Auth routes group
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth",
  component: AuthRoute,
});

// Login route
const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/login",
  component: Login,
});

// Protected index route
const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: Main,
});

function HubRouter() {
  const { page } = useSearch({ strict: false }) as { page?: "vacuum" };
  return page === "vacuum" ? <VacuumPage /> : <HubPage />;
}

// Wall display for an always-on phone or tablet (own full-screen layout).
const hubRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/hub",
  // /hub?page=vacuum is the vacuum's own page. (A nested path would break the
  // build's relative asset URLs, so it is a query parameter.)
  validateSearch: (s: Record<string, unknown>) => ({ page: s.page === "vacuum" ? ("vacuum" as const) : undefined }),
  component: HubRouter,
});

const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/public",
  component: () => <Main isPublic={true} />,
});

const routeTree = rootRoute.addChildren([
  protectedRoute.addChildren([indexRoute, hubRoute]),
  authRoute.addChildren([loginRoute]),
  publicRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  basepath: window.__BASE_URL__ || "/",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
